import { Box, IconButton, styled } from "@mui/material";
import { Check, Clear, List, Toolbox } from "@procore/core-icons";
import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars, useToolbarButtons } from "contexts/toolbars";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { PROCORE_BOTTOM_ENTRY, TOOLBAR_BUTTON, TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "../constants";
import { bottomToolbarActions, selectActiveTab, selectIsExpanded, ToolbarTab } from "./slice";

type ButtonVariant = "default" | "save" | "exit";

const TabIconButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== "active" && prop !== "variant",
})<{ active?: boolean; variant?: ButtonVariant }>(({ active, variant = "default" }) => {
    let bgColor: string = "transparent";
    let hoverBgColor: string = TOOLBAR_COLORS.HOVER;
    let color: string = TOOLBAR_COLORS.DEFAULT;

    if (variant === "save") {
        bgColor = active ? TOOLBAR_COLORS.SAVE_BG : "transparent";
        hoverBgColor = TOOLBAR_COLORS.SAVE_BG;
        color = TOOLBAR_COLORS.SAVE_GREEN;
    } else if (variant === "exit") {
        bgColor = active ? TOOLBAR_COLORS.EXIT_BG : "transparent";
        hoverBgColor = TOOLBAR_COLORS.EXIT_BG;
        color = TOOLBAR_COLORS.EXIT_RED;
    } else if (active) {
        bgColor = TOOLBAR_COLORS.ACTIVE_BG;
        hoverBgColor = TOOLBAR_COLORS.ACTIVE_HOVER;
        color = TOOLBAR_COLORS.ACTIVE;
    }

    return {
        width: `${PROCORE_BOTTOM_ENTRY.ICON_SIZE}px`,
        height: `${PROCORE_BOTTOM_ENTRY.ICON_SIZE}px`,
        padding: 0,
        borderRadius: `${TOOLBAR_BUTTON.RADIUS}px`,
        backgroundColor: bgColor,
        color: color,
        transition: "background-color 0.15s ease",
        "&:hover": {
            backgroundColor: hoverBgColor,
        },
        "& svg": {
            width: "24px",
            height: "24px",
        },
    };
});

const TabWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: ${PROCORE_BOTTOM_ENTRY.WIDTH}px;
`;

const TabLabel = styled("span", {
    shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ active }) => {
    let color: string = TOOLBAR_COLORS.DEFAULT;

    if (active) {
        color = TOOLBAR_COLORS.ACTIVE;
    }

    return {
        width: `${PROCORE_BOTTOM_ENTRY.TEXT_WIDTH}px`,
        height: `${PROCORE_BOTTOM_ENTRY.TEXT_HEIGHT}px`,
        fontSize: "10px",
        fontWeight: 500,
        color: color,
        textAlign: "center",
        lineHeight: `${PROCORE_BOTTOM_ENTRY.TEXT_HEIGHT}px`,
    };
});

const TabsContainer = styled(Box)`
    display: flex;
    gap: ${PROCORE_BOTTOM_ENTRY.GAP}px;
    height: ${PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT}px;
    padding: ${TOOLBAR_CONTAINER.PADDING}px;
    background-color: ${TOOLBAR_CONTAINER.BG};
    border-radius: ${TOOLBAR_CONTAINER.RADIUS}px;
    border: ${TOOLBAR_CONTAINER.BORDER};
    box-shadow: ${TOOLBAR_CONTAINER.SHADOW};
    pointer-events: auto;
    position: relative;
    z-index: 2;
    flex-shrink: 0;
`;

interface TabButtonRendererProps {
    toolbarId: string;
    activeTab: ToolbarTab;
    isExpanded: boolean;
}

/**
 * Custom button renderer for bottom toolbar tabs
 */
function TabButtonRenderer({ toolbarId, activeTab, isExpanded }: TabButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <TabsContainer>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (config.separator || !state.visible) {
                    return null;
                }

                // Determine variant and active state based on button ID
                let variant: ButtonVariant = "default";
                let isActive = state.active;

                if (config.id === "save") {
                    variant = "save";
                } else if (config.id === "exit") {
                    variant = "exit";
                } else {
                    // General/Tools tabs - active when expanded and matching activeTab
                    const isThisTab = config.id === activeTab;
                    isActive = isThisTab && isExpanded;
                }

                return (
                    <TabWrapper key={config.id}>
                        <TabIconButton
                            variant={variant}
                            active={isActive}
                            onClick={config.onClick}
                            title={config.title}
                        >
                            {config.icon}
                        </TabIconButton>
                        <TabLabel active={isActive}>{config.title}</TabLabel>
                    </TabWrapper>
                );
            })}
        </TabsContainer>
    );
}

interface BottomToolbarTabsProps {
    onSave?: () => void;
    onExit?: () => void;
}

export function BottomToolbarTabs({ onSave, onExit }: BottomToolbarTabsProps) {
    const dispatch = useAppDispatch();
    const toolbarDispatch = useDispatchToolbars();
    const activeTab = useAppSelector(selectActiveTab);
    const isExpanded = useAppSelector(selectIsExpanded);
    const showSaveExit = useAppSelector(selectActiveToolMode) !== ProcoreToolMode.Default;

    // Define buttons based on showSaveExit state
    const buttons: ToolbarButtonEntry[] = useMemo(() => {
        if (showSaveExit) {
            return [
                {
                    config: {
                        id: "save",
                        icon: <Check />,
                        onClick: onSave,
                        title: "Save",
                    },
                    state: {
                        active: false,
                        disabled: false,
                        visible: true,
                    },
                },
                {
                    config: {
                        id: "exit",
                        icon: <Clear />,
                        onClick: onExit,
                        title: "Exit",
                    },
                    state: {
                        active: false,
                        disabled: false,
                        visible: true,
                    },
                },
            ];
        }

        return [
            {
                config: {
                    id: "general",
                    icon: <List />,
                    onClick: () => dispatch(bottomToolbarActions.toggleTab("general")),
                    title: "General",
                },
                state: {
                    active: false, // Active state computed in renderer
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "tools",
                    icon: <Toolbox />,
                    onClick: () => dispatch(bottomToolbarActions.toggleTab("tools")),
                    title: "Tools",
                },
                state: {
                    active: false, // Active state computed in renderer
                    disabled: false,
                    visible: true,
                },
            },
        ];
    }, [showSaveExit, onSave, onExit, dispatch]);

    const toolbarId = "bottom-toolbar-tabs";

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(toolbarId));
        };
    }, [toolbarDispatch, buttons]);

    return <TabButtonRenderer toolbarId={toolbarId} activeTab={activeTab} isExpanded={isExpanded} />;
}
